/* Your people: who you want to see, how often, and when you both can.
 *
 * The list leads with whoever needs a plan — overdue, then due, then never
 * planned — and each row answers the three questions in the order people ask
 * them: how long has it been, when could we, and one button to make it happen.
 *
 * Nothing here is red and nothing counts down. Overdue is information on a list
 * you chose to open, not a notification that you are a bad friend.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, del, errorMessage } from '../../api';
import { useToast } from '../../ui/toast';
import Section from '../../ui/Section';
import Dialog from '../../ui/Dialog';
import Icon from '../../ui/Icon';
import { ConfirmDialog } from '../../ui/schedule';
import { friendlyDate, human, hhmm, toMinuteOfDay } from '../../ui/time';
import { useSession, localToday } from '../../session';

export interface Friend {
  id: string; name: string; personId: string | null;
  connected: boolean; canSeeFree: boolean;
  everyDays: number; closeness: 'favourite' | 'regular' | 'occasional';
  likes: string | null; prefer: 'evening' | 'weekend' | 'any'; minutes: number;
  lastSeen: string | null; daysSince: number | null; dueOn: string; daysLeft: number;
  state: 'overdue' | 'due' | 'new' | 'fine' | 'planned';
  planned: { date: string; startMin: number; start: string } | null;
  sentence: string;
}

export interface FreeSlot {
  date: string; startMin: number; endMin: number; start: string; end: string;
  weekday: string; dayOff: boolean; bothFree: boolean | null;
}

interface Person { id: string; name: string }

const EVERY = [
  { days: 7, label: 'Every week' },
  { days: 14, label: 'Every 2 weeks' },
  { days: 30, label: 'Every month' },
  { days: 60, label: 'Every 2 months' },
  { days: 90, label: 'Every 3 months' }
];
const everyLabel = (d: number) => EVERY.find(e => e.days === d)?.label ?? `Every ${d} days`;

const CLOSENESS: { value: Friend['closeness']; label: string }[] = [
  { value: 'favourite', label: 'Favourite' },
  { value: 'regular', label: 'Regular' },
  { value: 'occasional', label: 'Now and then' }
];
const PREFER: { value: Friend['prefer']; label: string; icon: 'moon' | 'sun' | 'clock' }[] = [
  { value: 'evening', label: 'Evenings', icon: 'moon' },
  { value: 'weekend', label: 'Weekends', icon: 'sun' },
  { value: 'any', label: 'Any free time', icon: 'clock' }
];
const LENGTHS = [60, 90, 120, 180, 240];

export const useFriends = () => useQuery({
  queryKey: ['friends'],
  queryFn: async () => {
    const r = await get<{ friends: Friend[] }>('/api/friends');
    if (!r.ok) throw new Error(errorMessage(r, 'Could not read your people.'));
    return r.friends;
  }
});

export default function Friends({ connections }: { connections: Person[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const friends = useFriends();
  const [editing, setEditing] = useState<Friend | 'new' | null>(null);
  const [planning, setPlanning] = useState<Friend | null>(null);
  const [dropping, setDropping] = useState<Friend | null>(null);

  const list = friends.data ?? [];
  const overdue = list.filter(f => f.state === 'overdue').length;
  const unused = connections.filter(c => !list.some(f => f.personId === c.id));

  const seen = useMutation({
    mutationFn: async (f: Friend) => {
      const r = await post(`/api/friends/${f.id}/seen`, {});
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
      return f;
    },
    onSuccess: f => {
      void qc.invalidateQueries({ queryKey: ['friends'] });
      toast.say(`Noted — you saw ${f.name} today.`, 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const remove = async (f: Friend) => {
    const r = await del(`/api/friends/${f.id}`);
    if (!r.ok) throw new Error(errorMessage(r, 'Could not remove that.'));
    void qc.invalidateQueries({ queryKey: ['friends'] });
    toast.say(`${f.name} is off the list. Anything already planned stays on your calendar.`);
  };

  return (
    <Section id="friends-title" icon="hobbies" className="tone-green" title="Your people"
             meta={list.length
               ? `${list.length} ${list.length === 1 ? 'person' : 'people'}${overdue ? ` · ${overdue} overdue` : ' · all on track'}`
               : 'who you want to see, and how often'}
             info={<>Say how often you want to see somebody and Amon keeps track of when you
               last did — from the evenings you actually planned together, or from "I saw them".
               When it has been too long it finds a time you are free; for somebody who shares
               their calendar with you, a time you are <strong>both</strong> free. Planning only
               ever touches your calendar — they get an invitation to answer.</>}
             action={
               <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
                 <Icon name="plus" size={16} />Add someone
               </button>
             }>

      {friends.isError && <p className="auth-err" role="alert">{(friends.error as Error).message}</p>}

      {!friends.isPending && list.length === 0 && (
        <div className="friend-empty">
          <p className="muted small">
            Nobody yet. Start with the one or two people a busy month quietly takes away.
          </p>
          {unused.length > 0 && (
            <div className="inline wrap" role="group" aria-label="Your connections">
              {unused.slice(0, 6).map(c => (
                <QuickAdd key={c.id} person={c} />
              ))}
            </div>
          )}
        </div>
      )}

      {list.length > 0 && (
        <ul className="friend-list">
          {list.map(f => (
            <li key={f.id} className={`friend state-${f.state}`}>
              <span className="friend-avatar" aria-hidden="true">
                {f.name.charAt(0).toUpperCase()}
                {f.closeness === 'favourite' && <span className="friend-star"><Icon name="star" size={10} /></span>}
              </span>

              <div className="friend-body">
                <div className="friend-head">
                  <strong>{f.name}</strong>
                  {f.closeness === 'favourite' && <span className="chip chip-soft">favourite</span>}
                  {f.connected && <span className="chip" title="On Amon">on Amon</span>}
                </div>
                <span className="muted small">
                  {everyLabel(f.everyDays)}
                  {f.likes ? ` · ${f.likes}` : ''}
                  {` · ${PREFER.find(p => p.value === f.prefer)?.label.toLowerCase()}`}
                </span>
                <span className={`friend-status status-${f.state}`}>
                  <Icon name={f.state === 'planned' ? 'calendar' : f.state === 'fine' ? 'check' : 'clock'} size={13} />
                  {f.state === 'planned' && f.planned
                    ? `Planned for ${friendlyDate(f.planned.date)} at ${f.planned.start}`
                    : f.sentence}
                </span>
              </div>

              <div className="friend-tools">
                {f.state !== 'planned' && (
                  <button className={`btn btn-sm ${f.state === 'overdue' || f.state === 'due' || f.state === 'new' ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => setPlanning(f)}
                          aria-label={`Plan time with ${f.name}`}>
                    <Icon name="calendar" size={15} />Plan it
                  </button>
                )}
                <button className="btn btn-ghost btn-icon" onClick={() => seen.mutate(f)}
                        aria-label={`I saw ${f.name} today`} title="I saw them today">
                  <Icon name="check2" size={17} />
                </button>
                <button className="btn btn-ghost btn-icon" onClick={() => setEditing(f)}
                        aria-label={`Edit ${f.name}`} title="Edit">
                  <Icon name="edit" size={17} />
                </button>
                <button className="btn btn-ghost btn-icon btn-bad" onClick={() => setDropping(f)}
                        aria-label={`Remove ${f.name}`} title="Remove">
                  <Icon name="trash" size={17} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <FriendForm existing={editing === 'new' ? null : editing}
                    connections={connections}
                    taken={list.map(f => f.personId).filter(Boolean) as string[]}
                    onClose={() => setEditing(null)} />
      )}

      {planning && <PlanFriend friend={planning} onClose={() => setPlanning(null)} />}

      {dropping && (
        <ConfirmDialog
          title={`Take ${dropping.name} off your list?`}
          description="Evenings you already planned together stay on your calendar. Amon just stops keeping track."
          confirmLabel="Take off the list"
          danger
          onClose={() => setDropping(null)}
          onConfirm={() => remove(dropping)} />
      )}
    </Section>
  );
}

/* One tap: a connection, every two weeks, evenings. Editable afterwards. */
function QuickAdd({ person }: { person: Person }) {
  const qc = useQueryClient();
  const toast = useToast();
  const add = useMutation({
    mutationFn: async () => {
      const r = await post('/api/friends', { personId: person.id, everyDays: 14 });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not add them.'));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['friends'] });
      toast.say(`${person.name} is on your list — every two weeks. Change it any time.`, 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });
  return (
    <button className="btn btn-ghost btn-sm" disabled={add.isPending} onClick={() => add.mutate()}>
      <Icon name="plus" size={14} />{person.name}
    </button>
  );
}

/* ── adding and editing ─────────────────────────────────────── */

function FriendForm({ existing, connections, taken, onClose }: {
  existing: Friend | null;
  connections: Person[];
  taken: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [personId, setPersonId] = useState(existing?.personId ?? '');
  const [name, setName] = useState(existing && !existing.personId ? existing.name : '');
  const [everyDays, setEveryDays] = useState(existing?.everyDays ?? 14);
  const [closeness, setCloseness] = useState<Friend['closeness']>(existing?.closeness ?? 'regular');
  const [likes, setLikes] = useState(existing?.likes ?? '');
  const [prefer, setPrefer] = useState<Friend['prefer']>(existing?.prefer ?? 'evening');
  const [minutes, setMinutes] = useState(existing?.minutes ?? 120);
  const [error, setError] = useState<string | null>(null);

  const choosable = connections.filter(c => c.id === existing?.personId || !taken.includes(c.id));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        personId: personId || null,
        ...(personId ? {} : { name: name.trim() }),
        everyDays, closeness, likes: likes.trim(), prefer, minutes
      };
      const r = existing
        ? await patch(`/api/friends/${existing.id}`, body)
        : await post('/api/friends', body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['friends'] });
      toast.say(existing ? 'Saved.' : 'Added. Amon will say when it has been too long.', 'good');
      onClose();
    },
    onError: (e: Error) => setError(e.message)
  });

  return (
    <Dialog title={existing ? `Edit ${existing.name}` : 'Someone you want to see more'} icon="hobbies"
            onClose={onClose}>
      <form className="when-form" onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (!personId && !name.trim()) { setError('Who is it?'); return; }
        setError(null);
        save.mutate();
      }}>
        {choosable.length > 0 && (
          <div className="field field-wide">
            <label htmlFor="friend-person">Who</label>
            <select id="friend-person" value={personId} onChange={e => setPersonId(e.target.value)}>
              <option value="">Somebody not on Amon</option>
              {choosable.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
        {!personId && (
          <div className="field field-wide">
            <label htmlFor="friend-name">{choosable.length ? 'Their name' : 'Who'}</label>
            <input id="friend-name" value={name} maxLength={80} placeholder="Dana"
                   onChange={e => setName(e.target.value)} />
          </div>
        )}

        <div className="field">
          <label htmlFor="friend-every">How often</label>
          <select id="friend-every" value={everyDays} onChange={e => setEveryDays(Number(e.target.value))}>
            {EVERY.map(o => <option key={o.days} value={o.days}>{o.label}</option>)}
          </select>
        </div>

        <div className="field">
          <label htmlFor="friend-minutes">For about</label>
          <select id="friend-minutes" value={minutes} onChange={e => setMinutes(Number(e.target.value))}>
            {LENGTHS.map(m => <option key={m} value={m}>{human(m)}</option>)}
          </select>
        </div>

        <fieldset className="field-wide repeat-picker">
          <legend>How close</legend>
          {CLOSENESS.map(c => (
            <label key={c.value} className={`chip chip-pick${closeness === c.value ? ' is-on' : ''}`}>
              <input type="radio" name="closeness" checked={closeness === c.value}
                     onChange={() => setCloseness(c.value)} />
              {c.value === 'favourite' && <Icon name="star" size={13} />}
              {c.label}
            </label>
          ))}
        </fieldset>

        <fieldset className="field-wide repeat-picker">
          <legend>When it tends to work</legend>
          {PREFER.map(p => (
            <label key={p.value} className={`chip chip-pick${prefer === p.value ? ' is-on' : ''}`}>
              <input type="radio" name="prefer" checked={prefer === p.value}
                     onChange={() => setPrefer(p.value)} />
              <Icon name={p.icon} size={13} />{p.label}
            </label>
          ))}
        </fieldset>

        <div className="field field-wide">
          <label htmlFor="friend-likes">What you like doing together <span className="muted">(optional)</span></label>
          <input id="friend-likes" value={likes} maxLength={120} placeholder="coffee, climbing"
                 onChange={e => setLikes(e.target.value)} />
        </div>

        {error && <p className="auth-err field-wide" role="alert">{error}</p>}

        <div className="inline field-wide">
          <button className="btn btn-primary btn-sm" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : existing ? 'Save' : 'Add'}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}

/* ── planning time together ─────────────────────────────────── */

export function PlanFriend({ friend, onClose }: { friend: Friend; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user, today } = useSession();
  const from = today ?? localToday(user?.tz);
  const [chosen, setChosen] = useState<FreeSlot | null>(null);
  const [custom, setCustom] = useState(false);
  const [date, setDate] = useState(from);
  const [start, setStart] = useState('19:00');
  const [invite, setInvite] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const times = useQuery({
    queryKey: ['friend-times', friend.id, from],
    queryFn: async () => {
      const r = await get<{ times: FreeSlot[]; canSeeFree: boolean }>(`/api/friends/${friend.id}/times?from=${from}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not find a time.'));
      return r;
    }
  });

  const slots = times.data?.times ?? [];
  const pick = custom ? null : (chosen ?? slots[0] ?? null);

  const save = useMutation({
    mutationFn: async () => {
      const body = pick
        ? { date: pick.date, startMin: pick.startMin, invite }
        : { date, startMin: toMinuteOfDay(start), invite };
      const r = await post<{ title: string; invited: { link: string | null; messaged: boolean } | null }>(
        `/api/friends/${friend.id}/plan`, body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not put that on your calendar.'));
      return r;
    },
    onSuccess: async r => {
      for (const k of ['friends', 'plan', 'week', 'month', 'meetings']) void qc.invalidateQueries({ queryKey: [k] });
      if (r.invited?.messaged) {
        toast.say(`“${r.title}” is on your calendar, and ${friend.name} has a message with the details.`, 'good');
      } else if (r.invited?.link) {
        let copied = false;
        try { await navigator.clipboard?.writeText(r.invited.link); copied = true; } catch { /* not allowed */ }
        toast.show({
          message: copied
            ? `“${r.title}” is on your calendar. Invite link copied — send it to ${friend.name}.`
            : `“${r.title}” is on your calendar. Send ${friend.name} this link: ${r.invited.link}`,
          tone: 'good',
          duration: 12000
        });
      } else {
        toast.say(`“${r.title}” is on your calendar.`, 'good');
      }
      onClose();
    },
    onError: (e: Error) => setError(e.message)
  });

  return (
    <Dialog title={`Time with ${friend.name}`} icon="calendar"
            description={friend.likes ? `${friend.likes} · about ${human(friend.minutes)}` : `About ${human(friend.minutes)}`}
            onClose={onClose}>
      <div className="when-form">
        <div className="field-wide">
          <p className="muted small plan-friend-note">
            <Icon name={times.data?.canSeeFree ? 'people' : 'eye'} size={14} />
            {times.data?.canSeeFree
              ? `These are times you and ${friend.name} are both free.`
              : friend.connected
                ? `${friend.name} doesn't share their calendar with you, so these are only times you are free.`
                : 'These are times you are free. They can answer the invitation with what works for them.'}
          </p>
        </div>

        {times.isPending && <p className="muted small field-wide">Finding a time…</p>}
        {times.isError && <p className="auth-err field-wide" role="alert">{(times.error as Error).message}</p>}
        {!times.isPending && !slots.length && !custom && (
          <p className="muted small field-wide">
            Nothing free in the next two weeks that fits. Pick a time yourself below.
          </p>
        )}

        {slots.length > 0 && (
          <fieldset className="field-wide slot-picker">
            <legend className="sr-only">Suggested times</legend>
            {slots.map(s => {
              const on = !custom && pick?.date === s.date && pick?.startMin === s.startMin;
              return (
                <label key={`${s.date}-${s.startMin}`} className={`slot-option${on ? ' is-on' : ''}`}>
                  <input type="radio" name="slot" checked={on}
                         onChange={() => { setCustom(false); setChosen(s); }} />
                  <span className="slot-when">
                    <strong>{friendlyDate(s.date)}</strong>
                    <span className="muted small">{s.start}–{s.end}</span>
                  </span>
                  {s.bothFree === true && <span className="chip chip-done"><Icon name="check" size={11} />both free</span>}
                </label>
              );
            })}
            <label className={`slot-option${custom ? ' is-on' : ''}`}>
              <input type="radio" name="slot" checked={custom} onChange={() => setCustom(true)} />
              <span className="slot-when"><strong>Another time</strong></span>
            </label>
          </fieldset>
        )}

        {(custom || (!times.isPending && !slots.length)) && (
          <>
            <div className="field">
              <label htmlFor="friend-date">Which day</label>
              <input id="friend-date" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="friend-start">Starts at</label>
              <input id="friend-start" type="time" value={start} onChange={e => setStart(e.target.value)} />
            </div>
          </>
        )}

        <label className="field-wide friend-invite">
          <input type="checkbox" checked={invite} onChange={e => setInvite(e.target.checked)} />
          <span>
            <strong>Send {friend.name} an invite</strong>
            <span className="muted small">
              {friend.connected ? ' — as a message on Amon, with a link to say yes.' : ' — a link you can send them.'}
            </span>
          </span>
        </label>

        {!custom && pick && (
          <p className="muted small field-wide">
            {friendlyDate(pick.date)}, {pick.start}–{pick.end}. Only your calendar is changed.
          </p>
        )}
        {(custom || !slots.length) && !times.isPending && (
          <p className="muted small field-wide">
            {friendlyDate(date)}, {start}–{hhmm(Math.min(1439, toMinuteOfDay(start) + friend.minutes))}.
          </p>
        )}

        {error && <p className="auth-err field-wide" role="alert">{error}</p>}

        <div className="inline field-wide">
          <button className="btn btn-primary btn-sm" disabled={save.isPending || times.isPending}
                  onClick={() => { setError(null); save.mutate(); }}>
            <Icon name="calendar" size={15} />{save.isPending ? 'Planning…' : 'Plan it'}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Dialog>
  );
}
